// ============================================================
// Concrete overlay layers, all built on PostOverlay (one config each).
//
// Product build ships ONLY the ascii veil:
//   • ascii — a glyph veil, bg-coloured, glowing only near the organism
//
// (The blob visualizer also carries gradient-map / glow / bloom / chroma / film
// configs; those are intentionally omitted here to keep the product surface
// minimal — one PostOverlay = one extra WebGL context.)
// ============================================================
import { PostOverlay } from "./postOverlay";
import type { PostConfig } from "./postOverlay";
import type { SliderDef } from "./types";

// Density-ordered glyph sets (dense → sparse). The first four are the original
// Framer presets; 4-6 are innernet's own MODES — the vocabulary of the dither
// changes with the section (binary/code over the developer act, an encryption
// cipher over privacy). More modes can be added here and later exposed to users
// to re-character their own netti.
const GLYPH_SETS: string[] = [
  "█▓▒░▇▆▅▄▃▂▁",   // 0 blocks
  "@%#*+=-:. ",     // 1 classic ASCII
  "●•∙· ",          // 2 dots
  "01#/:;=+*.-_ ",  // 3 technical
  "01",             // 4 BINARY — pure ones and zeros (the developer act)
  "10<>{}[]()/=;01+-01", // 5 CODE — binary + code punctuation, weighted to 0/1
  "0123456789ABCDEF#$%&@x", // 6 ENCRYPTION — hex + cipher symbols (privacy)
  "?",              // 7 QUESTIONS — a field of question marks (the faq)
];

// base palette (the Framer greens), rotated by the hue control
function rotateHue(rgb: [number, number, number], deg: number): [number, number, number] {
  const [r, g, b] = rgb;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  let h = 0, s = 0;
  const d = max - min;
  if (d > 1e-5) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  h = (h + deg / 360) % 1;
  const hue2 = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2(p, q, h + 1 / 3), hue2(p, q, h), hue2(p, q, h - 1 / 3)];
}
const FG_BASE: [number, number, number] = [0.910, 1.0, 0.906];   // #E8FFE7
const FG2_BASE: [number, number, number] = [0.698, 1.0, 0.831];  // #B2FFD4

// A faithful GLSL port of the Framer "ASCII Shader Overlay" field math: a
// generative depth/wave/grain/dither field per grid cell → luma → gamma →
// threshold → a density-ordered glyph, with color modes + finishing. Runs
// per-pixel (no DOM grid). Adds an "organism" term so the field can also brighten
// over the live body, and an "organism" colour mode that samples its palette.
const ASCII_FRAG = `
  uniform sampler2D u_atlas;
  uniform float u_atlasCols, u_atlasRows, u_atlasCount;
  uniform float u_cols, u_gamma, u_contrast, u_brightness, u_threshold;
  uniform float u_noise, u_dither, u_depth;
  uniform float u_driftX, u_driftY, u_waveAmp, u_waveFreq;
  uniform float u_flicker, u_mouseInf, u_organism, u_glyphScale, u_tint, u_fill, u_darkGlyph;
  uniform float u_invert, u_randomize, u_seed, u_te;
  uniform float u_colorMode, u_scanlines, u_vignette, u_glow;
  uniform float u_sparse;   // 1 = every cell, →0 = a sparse scatter of glyphs
  uniform vec3  u_fg, u_fg2;
  uniform vec2  u_presC;    // organism centre in uv (the "general area")
  uniform vec2  u_presR;    // elliptical radius of that area

  // ANALYTIC PRESENCE — we no longer sample the organism's pixels (that meant a
  // full-canvas readback + texture upload every frame). The dither only needs to
  // know the GENERAL AREA where netti is, so we fake it with a soft ellipse: 1 in
  // the body, feathering to 0 at the edge. Costs a length + smoothstep per pixel
  // instead of a texture fetch, and the organism context drops preserveDrawingBuffer.
  float bodyPresence(vec2 uv){
    float r = length((uv - u_presC) / max(vec2(0.02), u_presR));
    return clamp(1.0 - smoothstep(0.5, 1.0, r), 0.0, 1.0);
  }

  float h4(float s, float x, float y, float t){
    return fract(sin(s*12.9898 + x*78.233 + y*37.719 + t*0.017) * 43758.5453123);
  }
  vec3 hsl2rgb(float h, float s, float l){
    vec3 r = clamp(abs(mod(h/60.0 + vec3(0.0,4.0,2.0),6.0)-3.0)-1.0,0.0,1.0);
    return l + s*(r-0.5)*(1.0-abs(2.0*l-1.0));
  }
  // compact hue rotation (approximate, cheap) — the dithering radiates through
  // hue-shifted organism colours as it moves.
  vec3 hueRot(vec3 c, float a){
    float s=sin(a), co=cos(a);
    mat3 m = mat3(
      0.299+0.701*co+0.168*s, 0.587-0.587*co+0.330*s, 0.114-0.114*co-0.497*s,
      0.299-0.299*co-0.328*s, 0.587+0.413*co+0.035*s, 0.114-0.114*co+0.292*s,
      0.299-0.300*co+1.250*s, 0.587-0.588*co-1.050*s, 0.114+0.886*co-0.203*s);
    return clamp(m*c, 0.0, 1.0);
  }

  void main(){
    vec2 frag = v_uv * u_res;
    float cellW = u_res.x / max(8.0, u_cols);
    float cols = u_cols;
    float rows = max(6.0, floor(u_res.y / cellW));
    vec2 cell = floor(frag / cellW);
    vec2 cuv = fract(frag / cellW);
    float nx = cell.x / max(1.0, cols-1.0);
    float ny = cell.y / max(1.0, rows-1.0);

    // SPARSITY — keep only a scatter of cells (a stable per-cell hash gate that
    // drifts very slowly). The dither reads as spread-out points, not a solid
    // fill. u_sparse=1 keeps everything (classic dense); lower = sparser.
    float keep = h4(u_seed+555.0, cell.x*2.3, cell.y*2.9, floor(u_te*0.12));
    if (keep > clamp(u_sparse, 0.02, 1.0)) discard;

    float cx = 0.5 + u_driftX*0.15*sin(u_te*0.6);
    float cy = 0.5 + u_driftY*0.15*cos(u_te*0.6);
    float dx = nx-cx, dy = ny-cy;
    float radial = sqrt(dx*dx+dy*dy);
    float depth = exp(-radial * max(0.01,u_depth) * 3.5);
    float wave = sin((nx*8.0 + u_te*(0.7+u_driftX))*u_waveFreq) * cos((ny*8.0 + u_te*(0.8+u_driftY))*u_waveFreq);
    float grain = h4(u_seed, cell.x, cell.y, u_te*120.0) - 0.5;
    float dither = h4(u_seed+99.0, cell.x*1.7, cell.y*1.9, u_te*30.0) - 0.5;
    float mxf = u_mouse.x - nx, myf = u_mouse.y - ny;
    float mouseField = u_mouseInf>0.0 ? exp(-(mxf*mxf+myf*myf)*14.0)*u_mouseInf : 0.0;
    float pres = bodyPresence(v_uv);
    float org = pres * u_organism;

    // GROUND-LIT luminescence (colorMode 5, the default look): the glyph wears the
    // GROUND colour — invisible against the page — and only LIGHTS UP where it's
    // near the organism (presence) or under the pointer. Computing the glow now lets
    // the vast unlit field DISCARD before the atlas fetch below (a real perf win —
    // most cells cost nothing, so we can afford far more columns).
    float groundGlow = clamp(org * 1.35 + mouseField * 1.25, 0.0, 1.0);
    if (u_colorMode >= 4.5 && groundGlow < 0.02) discard;

    float luma = (depth*u_contrast + wave*u_waveAmp*0.5 + grain*u_noise + dither*u_dither + mouseField + org) * u_brightness;
    luma = pow(clamp(luma*0.9 + 0.5, 0.0, 1.0), 1.0/max(0.1,u_gamma));
    luma = luma > clamp(u_threshold,0.0,1.0) ? luma : luma*0.35;
    float mapped = u_invert>0.5 ? 1.0-luma : luma;

    float gi = floor(clamp(mapped,0.0,1.0) * (u_atlasCount-1.0));
    if (u_randomize>0.5) gi = clamp(gi + floor(h4(u_seed+777.0, cell.x, cell.y, u_te*40.0)*2.0), 0.0, u_atlasCount-1.0);

    float scale = clamp(1.0 + (mapped-0.5)*u_glyphScale, 0.45, 2.2);
    vec2 g = (cuv-0.5)/scale + 0.5;
    float coverage = 0.0;
    if (g.x>=0.0 && g.x<=1.0 && g.y>=0.0 && g.y<=1.0){
      float ac = mod(gi, u_atlasCols), ar = floor(gi/u_atlasCols);
      vec2 auv = (vec2(ac,ar)+vec2(g.x,1.0-g.y))/vec2(u_atlasCols,u_atlasRows);
      coverage = texture2D(u_atlas, auv).a;
      if (u_glow>0.0){
        vec2 hg = (g-0.5)/1.6 + 0.5;
        float halo = texture2D(u_atlas, (vec2(ac,ar)+vec2(hg.x,1.0-hg.y))/vec2(u_atlasCols,u_atlasRows)).a;
        coverage = max(coverage, halo*u_glow*0.55);
      }
    }
    if (coverage < 0.02) discard;

    vec3 col;
    if (u_colorMode < 0.5)      col = mix(u_fg, u_fg2, clamp(mapped*u_tint,0.0,1.0)*0.35);
    else if (u_colorMode < 1.5) col = mix(u_fg, u_fg2, clamp(ny*u_tint,0.0,1.0));
    else if (u_colorMode < 2.5) col = mix(u_fg2, u_fg, clamp((1.0-radial*1.35)*u_tint,0.0,1.0));
    else if (u_colorMode < 3.5) col = hsl2rgb(mod(u_te*30.0 + nx*160.0 + ny*120.0 + mapped*70.0,360.0), 0.8, 0.55+mapped*0.25);
    else if (u_colorMode < 4.5) {
      // "ORGANISM-LIT" dither — the living colour is generated from the veil's own
      // palette (u_fg/u_fg2), hue-shifted as it radiates, with NO framebuffer sample.
      vec3 base = mix(u_fg, u_fg2, clamp(mapped, 0.0, 1.0));
      col = hueRot(base, u_te*0.45 + radial*2.6) * (1.05 + 0.5*mapped);
      col = mix(col, vec3(1.0), 0.12*mapped);        // glow crest on the bright cells
    }
    else {
      // GROUND-LIT (default) — the glyph IS the ground colour, so it disappears into
      // the page; it only luminesces toward light where the organism is or the
      // pointer hovers. "just the numbers light up."
      vec3 lit = mix(clamp(u_bg, 0.0, 1.0), vec3(1.0), 0.94);
      col = mix(clamp(u_bg, 0.0, 1.0), lit, groundGlow);
    }

    // ground mode owns its colour + alpha (below); the ink-darkening passes are for
    // the palette modes only, so they don't fight the ground-lit look.
    if (u_colorMode < 4.5) {
      // DARK-GLYPH — on a LIGHT ground the pale glyphs vanish; drive the ink toward
      // a deep tone (keeping its hue) so it reads as a dark cipher on white.
      col = mix(col, col * 0.16 + vec3(0.015), clamp(u_darkGlyph, 0.0, 1.0));
      // AUTO — darken toward the hue on light grounds so the veil always reads.
      col = mix(col, col * 0.2 + vec3(0.014), smoothstep(0.52, 0.82, dot(clamp(u_bg, 0.0, 1.0), vec3(0.299, 0.587, 0.114))));
    }

    float flick = 1.0 - u_flicker + u_flicker*(0.6 + h4(u_seed+123.0, cell.x, cell.y, u_te*220.0)*0.8);
    float a = clamp(0.18 + mapped*0.82, 0.0, 1.0) * flick * coverage * u_opacity;
    // ORGANISM REACH — the dither lives on the organism (absent on the bare ground).
    // u_fill widens how much of the body it occupies. In GROUND mode the glow itself
    // is the mask (unlit cells already discarded), so alpha follows the luminescence.
    if (u_colorMode >= 4.5) {
      a = (0.04 + 0.96 * groundGlow) * coverage * u_opacity * flick;
    } else {
      float bodyMask = smoothstep(0.012, mix(0.5, 0.12, clamp(u_fill,0.0,1.0)), pres);
      a *= bodyMask;
    }
    a *= mix(1.0, 0.55 + 0.45*sin(frag.y*1.6), u_scanlines);
    float vig = smoothstep(1.4, 0.4, length((v_uv-0.5)*2.0));
    col = mix(col, col*vig, u_vignette);

    gl_FragColor = vec4(col, a);
  }
`;

function buildAsciiAtlas(gl: WebGLRenderingContext, chars: string): { tex: WebGLTexture; cols: number; rows: number; count: number } {
  const COLS = Math.min(8, Math.max(1, chars.length));
  const ROWS = Math.ceil(chars.length / COLS);
  const CELL = 56;
  const cv = document.createElement("canvas");
  cv.width = COLS * CELL;
  cv.height = ROWS * CELL;
  const ctx = cv.getContext("2d")!;
  ctx.clearRect(0, 0, cv.width, cv.height);
  ctx.fillStyle = "#fff";
  ctx.font = `${Math.round(CELL * 0.82)}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < chars.length; i++) {
    const c = i % COLS, r = Math.floor(i / COLS);
    ctx.fillText(chars[i], c * CELL + CELL / 2, r * CELL + CELL / 2);
  }
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return { tex, cols: COLS, rows: ROWS, count: chars.length };
}

// The veil's tunable controls — the ONE source of truth for the customization
// surface. Shared by asciiConfig (the live overlay) AND the /netti appearance
// panel (which renders these and dispatches `innernet:veil-tune` to update the
// running organism live). `primary` sliders lead; the rest hide behind "more".
export const VEIL_SLIDERS: SliderDef[] = [
  { key: "preset", label: "glyphs", min: 0, max: 7, step: 1, hint: "the glyph set", kind: "enum", options: ["blocks", "classic", "dots", "technical", "binary", "code", "encryption", "questions"] , primary: true },
  { key: "colorMode", label: "colour", min: 0, max: 5, step: 1, hint: "how cells are tinted", kind: "enum", options: ["single", "vertical", "radial", "rainbow", "organism", "ground"] , primary: true },
  { key: "blend", label: "blend", min: 0, max: 5, step: 1, hint: "composite mode", kind: "enum", options: ["normal", "screen", "lighten", "plus", "overlay", "soft"] },
  { key: "columns", label: "columns", min: 24, max: 240, step: 1, hint: "coarse ←→ fine grid" , primary: true },
  { key: "sparse", label: "density", min: 0.02, max: 1, step: 0.01, hint: "sparse scatter ←→ full fill" , primary: true },
  { key: "organism", label: "follow body", min: 0, max: 2, step: 0.01, hint: "free field ←→ glows on the organism" , primary: true },
  { key: "fill", label: "fill", min: 0, max: 1, step: 0.01, hint: "hug the heart ←→ fill the whole organism" , primary: true },
  { key: "depth", label: "depth", min: 0.2, max: 2.5, step: 0.01, hint: "flat ←→ deep falloff" },
  { key: "contrast", label: "contrast", min: 0, max: 2, step: 0.01, hint: "flat ←→ punchy" },
  { key: "brightness", label: "brightness", min: 0, max: 2, step: 0.01, hint: "dim ←→ bright" },
  { key: "gamma", label: "gamma", min: 0.2, max: 3, step: 0.01, hint: "lift ←→ crush" },
  { key: "threshold", label: "threshold", min: 0, max: 1, step: 0.01, hint: "soft ←→ hard cut" },
  { key: "noise", label: "noise", min: 0, max: 1, step: 0.01, hint: "clean ←→ grainy" },
  { key: "dither", label: "dither", min: 0, max: 1, step: 0.01, hint: "smooth ←→ stippled" },
  { key: "speed", label: "speed", min: 0, max: 4, step: 0.01, hint: "still ←→ fast" , primary: true },
  { key: "driftX", label: "drift x", min: -2, max: 2, step: 0.01, hint: "horizontal flow" },
  { key: "driftY", label: "drift y", min: -2, max: 2, step: 0.01, hint: "vertical flow" },
  { key: "waveAmp", label: "wave amp", min: 0, max: 2, step: 0.01, hint: "calm ←→ rolling" },
  { key: "waveFreq", label: "wave freq", min: 0.2, max: 8, step: 0.01, hint: "long ←→ tight waves" },
  { key: "flicker", label: "flicker", min: 0, max: 1, step: 0.01, hint: "steady ←→ alive" },
  { key: "mouse", label: "mouse", min: 0, max: 2, step: 0.01, hint: "pointer brightening" },
  { key: "glyphScale", label: "glyph scale", min: 0, max: 2, step: 0.01, hint: "uniform ←→ density-scaled" },
  { key: "hue", label: "hue", min: 0, max: 360, step: 1, hint: "rotate the palette" , primary: true },
  { key: "tint", label: "tint", min: 0, max: 1, step: 0.01, hint: "mono ←→ two-tone" },
  { key: "glow", label: "glow", min: 0, max: 2, step: 0.01, hint: "matte ←→ luminous" , primary: true },
  { key: "scanlines", label: "scanlines", min: 0, max: 1, step: 0.01, hint: "off ←→ CRT" },
  { key: "vignette", label: "vignette", min: 0, max: 1, step: 0.01, hint: "off ←→ framed" },
  { key: "invert", label: "invert", min: 0, max: 1, step: 1, hint: "flip density", kind: "toggle" },
  { key: "randomize", label: "randomize", min: 0, max: 1, step: 1, hint: "glyph jitter", kind: "toggle" },
  { key: "animate", label: "animate", min: 0, max: 1, step: 1, hint: "freeze ←→ live", kind: "toggle" , primary: true },
  { key: "opacity", label: "opacity", min: 0, max: 1, step: 0.01, hint: "faint ←→ present" , primary: true },
];

export function asciiConfig(): PostConfig {
  let glState: WebGLRenderingContext | null = null;
  let prog: WebGLProgram | null = null;
  let atlas: { tex: WebGLTexture; cols: number; rows: number; count: number } | null = null;
  let builtPreset = -1;
  const rebuild = (preset: number) => {
    if (!glState || !prog) return;
    const chars = GLYPH_SETS[preset] ?? GLYPH_SETS[0];
    if (atlas) glState.deleteTexture(atlas.tex);
    atlas = buildAsciiAtlas(glState, chars);
    glState.useProgram(prog);
    glState.uniform1f(glState.getUniformLocation(prog, "u_atlasCols"), atlas.cols);
    glState.uniform1f(glState.getUniformLocation(prog, "u_atlasRows"), atlas.rows);
    glState.uniform1f(glState.getUniformLocation(prog, "u_atlasCount"), atlas.count);
    builtPreset = preset;
  };
  return {
    id: "ascii",
    label: "ascii",
    zIndex: 6,
    frag: ASCII_FRAG,
    // the veil generates its whole field procedurally + fakes netti's location
    // with an analytic mask — it never reads the organism's pixels, so skip the
    // per-frame framebuffer readback/upload entirely.
    samplesSource: false,
    blendCss: ["normal", "screen", "lighten", "plus-lighter", "overlay", "soft-light"],
    uniforms: [
      "u_atlas", "u_atlasCols", "u_atlasRows", "u_atlasCount",
      "u_cols", "u_gamma", "u_contrast", "u_brightness", "u_threshold",
      "u_noise", "u_dither", "u_depth", "u_driftX", "u_driftY", "u_waveAmp", "u_waveFreq",
      "u_flicker", "u_mouseInf", "u_organism", "u_glyphScale", "u_tint", "u_fill", "u_darkGlyph",
      "u_invert", "u_randomize", "u_seed", "u_te", "u_colorMode", "u_scanlines", "u_vignette", "u_glow", "u_sparse", "u_fg", "u_fg2",
      "u_presC", "u_presR",
    ],
    // numeric params (enums/toggles store an index/0-1)
    params: {
      // AUTO-APPLIED default — the CLASSIC dithering look, applied tastefully and
      // SPARINGLY: a scatter of small classic glyphs (u_sparse) over the organism
      // only (masked to its presence), so it reads as spread-out data-points, not a
      // fill. Each shown glyph is crisp + present; the coverage is thin. A section
      // can boost/re-character it live via the `innernet:veil-tune` bus.
      preset: 1, columns: 150, colorMode: 4, blend: 0,
      gamma: 1.1, contrast: 1.35, brightness: 1.05, threshold: 0.34,
      noise: 0.1, dither: 0.1, depth: 1.1, sparse: 0.42,
      speed: 1.0, driftX: 0.35, driftY: -0.22, waveAmp: 0.6, waveFreq: 2.2,
      flicker: 0.2, mouse: 0.28, organism: 0.72, glyphScale: 0.5, tint: 0.7,
      hue: 237, glow: 1.15, scanlines: 0.0, vignette: 0.0, fill: 0.25, darkGlyph: 0.0,
      invert: 0, randomize: 1, animate: 1, seed: 1337, opacity: 0.9,   // present, but sparse
      // analytic "where netti is" — a soft ellipse (uv space) the dither floats
      // within. The organism canvas is square + roughly centred, so a centred blob
      // ~0.42 radius covers the body; a surface can nudge these live if needed.
      presX: 0.5, presY: 0.5, presR: 0.42,
    },
    sliders: VEIL_SLIDERS,
    hooks: {
      onInit: (gl, program) => {
        glState = gl; prog = program;
        gl.useProgram(program);
        gl.uniform1i(gl.getUniformLocation(program, "u_atlas"), 1);
        rebuild(0);
      },
      onFrame: (gl, u, p, t) => {
        const preset = Math.round(p.preset);
        if (preset !== builtPreset) rebuild(preset);
        if (atlas) { gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, atlas.tex); gl.uniform1i(u.u_atlas, 1); }
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        gl.uniform1f(u.u_cols, Math.max(8, p.columns) * 1.0);
        gl.uniform1f(u.u_gamma, p.gamma); gl.uniform1f(u.u_contrast, p.contrast);
        gl.uniform1f(u.u_brightness, p.brightness); gl.uniform1f(u.u_threshold, p.threshold);
        gl.uniform1f(u.u_noise, p.noise); gl.uniform1f(u.u_dither, p.dither); gl.uniform1f(u.u_depth, p.depth);
        gl.uniform1f(u.u_driftX, p.driftX); gl.uniform1f(u.u_driftY, p.driftY);
        gl.uniform1f(u.u_waveAmp, p.waveAmp); gl.uniform1f(u.u_waveFreq, p.waveFreq);
        gl.uniform1f(u.u_flicker, p.flicker); gl.uniform1f(u.u_mouseInf, p.mouse);
        gl.uniform1f(u.u_organism, p.organism); gl.uniform1f(u.u_glyphScale, p.glyphScale);
        gl.uniform1f(u.u_fill, p.fill ?? 0.25);
        gl.uniform1f(u.u_darkGlyph, p.darkGlyph ?? 0.0);
        gl.uniform1f(u.u_tint, p.tint); gl.uniform1f(u.u_invert, p.invert);
        gl.uniform1f(u.u_randomize, p.randomize); gl.uniform1f(u.u_seed, p.seed);
        gl.uniform1f(u.u_colorMode, p.colorMode); gl.uniform1f(u.u_scanlines, p.scanlines);
        gl.uniform1f(u.u_vignette, p.vignette); gl.uniform1f(u.u_glow, p.glow);
        gl.uniform1f(u.u_sparse, p.sparse ?? 1.0);
        gl.uniform2f(u.u_presC, p.presX ?? 0.5, p.presY ?? 0.5);
        // a touch taller than wide reads more like the upright bloom
        gl.uniform2f(u.u_presR, p.presR ?? 0.42, (p.presR ?? 0.42) * 1.12);
        gl.uniform1f(u.u_te, p.animate > 0.5 ? t * (p.speed ?? 1) : 0);
        const fg = rotateHue(FG_BASE, p.hue), fg2 = rotateHue(FG2_BASE, p.hue);
        gl.uniform3f(u.u_fg, fg[0], fg[1], fg[2]);
        gl.uniform3f(u.u_fg2, fg2[0], fg2[1], fg2[2]);
        void dpr;
      },
    },
  };
}

export function makeAscii(canvas: HTMLCanvasElement, source: HTMLCanvasElement, reservedRight: number): PostOverlay {
  return new PostOverlay(canvas, source, asciiConfig(), { reservedRight });
}

// ── THE VEIL LOOK — DOTS, GROUND-LIT (John's direction, 2026-07-09) ───────────
// One quiet look everywhere: a fine field of DOTS that wears the GROUND colour, so
// it's invisible against the page and only LIGHTS UP where it's near the organism
// or under the pointer ("just the numbers light up"). No code/binary/technical
// glyph modes in the default flow. More columns (finer, higher-res) but LIGHTER +
// sparser, and cheaper — the ground-lit early-discard skips the whole unlit field.
export const VEIL_BASE: Record<string, number> = {
  colorMode: 5, blend: 2, columns: 210, sparse: 0.30, organism: 1.0, fill: 0.5,
  depth: 1.2, contrast: 1.05, brightness: 1.0, gamma: 1.0, threshold: 0.28,
  noise: 0.08, dither: 0.12, speed: 1.0, driftX: 0.3, driftY: -0.2,
  waveAmp: 0.5, waveFreq: 2.2, flicker: 0.16, mouse: 1.15, glyphScale: 0.0,
  hue: 240, tint: 0.3, glow: 0.5, scanlines: 0.0, vignette: 0.0,
  opacity: 0.82, animate: 1, randomize: 0, invert: 0, darkGlyph: 0,
};

// THE DEFAULT NETTI LOOK — the calm ground-lit DOTS. This is what netti wears
// everywhere the app renders the organism (dashboard + netti lab + landing). The
// /netti appearance panel starts here and lets the user re-tune every property.
export const VEIL_DOTS: Record<string, number> = {
  ...VEIL_BASE, preset: 2, seed: 1337,
};

export type VeilSection = "hero" | "remember" | "everyone" | "dev" | "priv" | "cinematic" | "faq";

// ONE look for every section — the ground-lit dots. The HERO gets NO dithering at
// all (opacity 0): there, the grown, dense organism carries the whole stage. The
// dense-copy acts (dev/privacy) drop the dots a touch so they never fight the words.
export function veilForSection(section: VeilSection): Record<string, number> {
  const patch: Record<string, number> = { ...VEIL_DOTS };
  if (section === "hero") {
    patch.opacity = 0; // the hero is pure organism — no veil
  } else if (section === "dev" || section === "priv" || section === "cinematic") {
    patch.opacity = 0.6; // quieter where copy sits directly over netti
  }
  return patch;
}
